import { ListSkeleton, SectionCard } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { movedOnePlace } from '@/domain/reorder'
import type { WorkflowStatusUpdateBody, WorkflowTransitionUpdateBody } from '@/domain/workflow-admin.types'
import {
	useReorderWorkflowStatuses,
	useUpdateWorkflow,
	useUpdateWorkflowStatus,
	useUpdateWorkflowTransition,
	useWorkflow,
	useWorkflowStatuses,
	useWorkflowTransitions,
} from '@/hooks/useWorkflowAdmin'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'
import { TextField } from '@/pages/Classification/FormField'
import { StatusList } from './StatusList'
import { ErrorBanner, ErrorState } from './states'
import { TransitionList } from './TransitionList'

/**
 * One workflow's configuration: its metadata, its statuses, and the transitions
 * between them. Lifecycle (activate, deactivate, delete, default) lives on the
 * list page, because those are dedicated operations rather than metadata edits.
 */

/**
 * A code the backend refused because it is still referenced comes back as a
 * validation error on that field. Anything mentioning the code field is shown
 * against the input; everything else stays a whole-form error.
 */
function codeFieldErrorOf(error: unknown): string | undefined {
	if (!error) return undefined
	if (apiErrorStatus(error) !== 422) return undefined
	const message = apiErrorMessage(error)
	return /code/i.test(message) ? message : undefined
}

function MetadataForm({ workflowId, name }: { workflowId: string; name: string }) {
	const update = useUpdateWorkflow()
	const [draft, setDraft] = useState(name)
	useEffect(() => setDraft(name), [name])
	return (
		<SectionCard title="Workflow metadata" data-testid="workflow-metadata">
			<div className="flex flex-wrap items-end gap-3">
				<TextField label="Name" value={draft} onChange={setDraft} />
				<Button
					isDisabled={update.isPending || draft.trim().length === 0 || draft === name}
					onPress={() => update.mutate({ id: workflowId, body: { name: draft.trim() } })}
				>
					{update.isPending ? 'Saving…' : 'Save'}
				</Button>
				{update.isError && <ErrorBanner error={update.error} title="Could not save metadata" />}
			</div>
		</SectionCard>
	)
}

function StatusesSection({ workflowId }: { workflowId: string }) {
	const statuses = useWorkflowStatuses(workflowId)
	const update = useUpdateWorkflowStatus()
	const reorder = useReorderWorkflowStatuses()
	const save = (statusId: string, body: WorkflowStatusUpdateBody) => update.mutate({ statusId, workflowId, body })
	// The reorder takes the workflow's complete active order and returns the
	// authoritative one, so the list is refetched rather than reordered locally.
	const move = (statusId: string, direction: 'up' | 'down') => {
		const activeOrder = (statuses.data ?? [])
			.filter((status) => status.isActive)
			.slice()
			.sort((a, b) => a.position - b.position)
			.map((status) => status.statusId)
		reorder.mutate({
			workflowId,
			statusIds: movedOnePlace(activeOrder, statusId, direction),
		})
	}
	return (
		<SectionCard title="Statuses">
			{reorder.isError && <ErrorBanner error={reorder.error} title="Could not reorder statuses" />}
			<StatusList
				statuses={statuses.data ?? []}
				isLoading={statuses.isLoading}
				error={statuses.isError ? statuses.error : undefined}
				onRetry={() => void statuses.refetch()}
				onSave={save}
				onMove={move}
				isReordering={reorder.isPending}
				pendingStatusId={update.isPending ? (update.variables?.statusId ?? null) : null}
				fieldErrors={update.isError ? { code: codeFieldErrorOf(update.error) } : undefined}
				saveError={update.isError ? update.error : undefined}
			/>
		</SectionCard>
	)
}

function TransitionsSection({ workflowId }: { workflowId: string }) {
	const statuses = useWorkflowStatuses(workflowId)
	const transitions = useWorkflowTransitions(workflowId)
	const update = useUpdateWorkflowTransition()
	const save = (transitionId: string, body: WorkflowTransitionUpdateBody) =>
		update.mutate({ transitionId, workflowId, body })
	return (
		<SectionCard title="Transitions">
			<TransitionList
				transitions={transitions.data ?? []}
				statuses={statuses.data ?? []}
				isLoading={transitions.isLoading}
				error={transitions.isError ? transitions.error : undefined}
				onRetry={() => void transitions.refetch()}
				onSave={save}
				pendingTransitionId={update.isPending ? (update.variables?.transitionId ?? null) : null}
				saveError={update.isError ? update.error : undefined}
			/>
		</SectionCard>
	)
}

export function WorkflowDetailPage() {
	const { workflowId = null } = useParams<{ workflowId: string }>()
	const workflow = useWorkflow(workflowId)

	if (workflow.isLoading) return <ListSkeleton label="Loading workflow" />
	if (workflow.isError) {
		return <ErrorState error={workflow.error} onRetry={() => void workflow.refetch()} title="Could not load workflow" />
	}
	if (!workflow.data || !workflowId) return null

	return (
		<div className="space-y-6">
			<div>
				<Link className="text-sm text-ds-primary hover:underline" to="/admin/workflows">
					← All workflows
				</Link>
				<h1 className="text-2xl font-semibold">{workflow.data.name}</h1>
				<p className="text-ds-text-muted">{workflow.data.code}</p>
			</div>
			<MetadataForm workflowId={workflowId} name={workflow.data.name} />
			<StatusesSection workflowId={workflowId} />
			<TransitionsSection workflowId={workflowId} />
		</div>
	)
}
